import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import {
  ClearanceRejectionReasonEnum,
  ClearanceStatusEnum,
  ConnectionClearanceWrite,
} from '../../../api';
import { generateZEnumFromEnum } from '../../../utils/forms';
import { FormSelectField } from '../FormSelectField';
import { FormTextField } from '../FormTextField';

interface DenyClearanceDialogProps {
  show: boolean;
  closeDialog: (result: ConnectionClearanceWrite | null) => void;
}

const validationSchema = z.object({
  rejection_reason: z.union([
    z.null(),
    z.string().length(1, 'Reason is not selected. Please choose one from the list.'),
    generateZEnumFromEnum(window.CLEARANCES_REJECTION_REASONS),
  ]),
  rejection_details: z.string().optional(),
  present_date: z.union([
    z.string().length(0),
    z.null(),
    z.coerce.date().transform((val: Date) => {
      return new Date(val.valueOf() + val.getTimezoneOffset() * 60 * 1000);
    }),
  ]),
});

export const DenyClearanceDialog = ({ show, closeDialog }: DenyClearanceDialogProps) => {
  const [showServerFieldErrors, setShowServerFieldErrors] = useState<{
    rejection_reason?: boolean;
    rejection_details?: boolean;
  }>({});

  const {
    clearErrors,
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(validationSchema),
  });

  const reason = useWatch({ control, name: 'rejection_reason' });

  const handleSubmitClick = () => {
    clearErrors();
    setShowServerFieldErrors({});
  };

  const handleClose = () => {
    reset({
      rejection_reason: '',
      details: '',
      present_date: '',
    });

    closeDialog(null);
  };

  const onSubmit = async (data) => {
    closeDialog({
      clearance_status: ClearanceStatusEnum.NOT_CLEARED,
      clearance_rejection_reason: data.rejection_reason,
      clearance_rejection_details: data.rejection_details || '',
      clearance_date: data.present_date || undefined,
    });

    reset({
      rejection_reason: '',
      rejection_details: '',
      present_date: '',
    });
  };

  return (
    <Modal show={show} onHide={() => handleClose()}>
      <Modal.Header closeButton>
        <Modal.Title>Deny Clearance</Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body style={{ maxHeight: '60vh' }}>
          <FormSelectField
            name="rejection_reason"
            label="Not cleared because:"
            placeholder="Select a reason"
            options={window.CLEARANCES_REJECTION_REASONS}
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            defaultValue={''}
            errors={errors}
            register={register}
          />
          {reason === ClearanceRejectionReasonEnum.OTHER && (
            <Form.Group>
              <Form.Label>Other reason for the denial</Form.Label>
              <Form.Control
                type="text"
                as="textarea"
                placeholder="Enter a reason"
                {...register('rejection_details')}
              />
            </Form.Group>
          )}
          <FormTextField
            type="date"
            name={`present_date`}
            label="Provider can be presented again"
            showServerFieldErrors={showServerFieldErrors}
            setShowServerFieldErrors={setShowServerFieldErrors}
            errors={errors}
            register={register}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" onClick={() => handleSubmitClick()}>
            Confirm
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
